import type { VerificationRank } from '../types/verification';

/**
 * 地域エリアマッピング
 */
export const REGION_PREFECTURES = {
  '北海道・東北': ['北海道', '青森県', '岩手県', '宮城県', '秋田県', '山形県', '福島県'],
  '関東': ['茨城県', '栃木県', '群馬県', '埼玉県', '千葉県', '東京都', '神奈川県'],
  '中部': ['新潟県', '富山県', '石川県', '福井県', '山梨県', '長野県', '岐阜県', '静岡県', '愛知県'],
  '関西': ['三重県', '滋賀県', '京都府', '大阪府', '兵庫県', '奈良県', '和歌山県'],
  '中国': ['鳥取県', '島根県', '岡山県', '広島県', '山口県'],
  '四国': ['徳島県', '香川県', '愛媛県', '高知県'],
  '九州・沖縄': ['福岡県', '佐賀県', '長崎県', '熊本県', '大分県', '宮崎県', '鹿児島県', '沖縄県'],
} as const;

export type RegionName = keyof typeof REGION_PREFECTURES;

/**
 * 都道府県から地域を取得
 */
export const getRegionByPrefecture = (prefecture: string): RegionName | null => {
  for (const [region, prefectures] of Object.entries(REGION_PREFECTURES)) {
    if ((prefectures as readonly string[]).includes(prefecture)) {
      return region as RegionName;
    }
  }
  return null;
};

/**
 * 都道府県一覧
 */
export const PREFECTURES = [
  '北海道',
  '青森県',
  '岩手県',
  '宮城県',
  '秋田県',
  '山形県',
  '福島県',
  '茨城県',
  '栃木県',
  '群馬県',
  '埼玉県',
  '千葉県',
  '東京都',
  '神奈川県',
  '新潟県',
  '富山県',
  '石川県',
  '福井県',
  '山梨県',
  '長野県',
  '岐阜県',
  '静岡県',
  '愛知県',
  '三重県',
  '滋賀県',
  '京都府',
  '大阪府',
  '兵庫県',
  '奈良県',
  '和歌山県',
  '鳥取県',
  '島根県',
  '岡山県',
  '広島県',
  '山口県',
  '徳島県',
  '香川県',
  '愛媛県',
  '高知県',
  '福岡県',
  '佐賀県',
  '長崎県',
  '熊本県',
  '大分県',
  '宮崎県',
  '鹿児島県',
  '沖縄県',
] as const;

/**
 * 血液型
 */
export const BLOOD_TYPES = ['A', 'B', 'O', 'AB'] as const;

/**
 * 血液型 Rh
 */
export const BLOOD_TYPE_RH = ['+', '-', 'unknown'] as const;

/**
 * 学歴レベル
 */
export const EDUCATION_LEVELS = ['高校', '専門学校', '大学', '大学院'] as const;

/**
 * 卒業状況
 */
export const EDUCATION_STATUSES = ['卒業', '在学中', '中退'] as const;

/**
 * 国籍
 */
export const NATIONALITIES = ['日本', '日本以外'] as const;

/**
 * 交通費負担
 */
export const TRAVEL_EXPENSE_OPTIONS = ['選択してください', 'ドナーが負担', 'ドニーが負担', '双方で負担'] as const;

/**
 * 年収レンジ
 */
export const ANNUAL_INCOME_OPTIONS = [
  '400万円未満',
  '400万円〜600万円',
  '600万円〜800万円',
  '800万円〜1000万円',
  '1000万円〜1500万円',
  '1500万円〜2000万円',
  '2000万円以上',
] as const;

/**
 * 婚姻状況
 */
export const MARITAL_STATUSES = ['未婚', '既婚', '離婚'] as const;

/**
 * 血統
 */
export const ANCESTRIES = ['純日本人', 'ハーフ', 'クォーター', 'その他'] as const;

/**
 * 体型
 */
export const BODY_TYPES = ['痩せ型', '普通', '筋肉質', 'ぽっちゃり'] as const;

/**
 * 髪質
 */
export const HAIR_TYPES = ['ストレート', 'カーリー', 'ソフト', 'ハード', 'その他'] as const;

/**
 * 髪の色
 */
export const HAIR_COLORS = ['黒色', '茶色', 'その他'] as const;

/**
 * まぶた
 */
export const EYELIDS = ['一重', '二重', '奥二重', 'その他'] as const;

/**
 * 提供方法
 */
export const DONATION_METHODS = [
  { value: 'syringe', label: 'シリンジ法' },
  { value: 'timing', label: 'タイミング法' },
] as const;

/**
 * 提供地域の希望（ドニー用）
 */
export const PREFERRED_LOCATIONS = [
  '未回答',
  'ドナーの地域に向かう',
  'ドナーが来てほしい',
  '相談して決める',
] as const;

/**
 * 身元開示
 */
export const IDENTITY_DISCLOSURE_OPTIONS = ['開示意志あり', '開示意志なし'] as const;

/**
 * ファイルサイズ制限
 */
export const FILE_SIZE_LIMITS = {
  PROFILE_IMAGE: 5 * 1024 * 1024, // 5MB
  VERIFICATION_DOCUMENT: 10 * 1024 * 1024, // 10MB
  MESSAGE_IMAGE: 5 * 1024 * 1024, // 5MB
} as const;

/**
 * 画像の許可されるMIMEタイプ
 */
export const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'] as const;

/**
 * ドキュメントの許可されるMIMEタイプ
 */
export const ALLOWED_DOCUMENT_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'application/pdf',
] as const;

/**
 * ユーザーステータス表示名
 */
export const USER_STATUS_LABELS = {
  pending: '登録中',
  email_verified: 'メール認証済み',
  under_review: '審査中',
  approved: '本会員',
  rejected: '審査却下',
  suspended: '停止中',
  withdrawn: '退会済み',
} as const;

/**
 * 認証バッジ表示名
 */
export const VERIFICATION_LABELS = {
  identity: '本人確認',
  education: '学歴証明',
  income: '年収証明',
  spermTest: '精液検査',
  stdTest: '性病検査',
} as const;

/**
 * 認証ランク
 */
export const VERIFICATION_RANKS = ['A', 'B', 'C', 'D'] as const;

/**
 * スコアリング設定
 */
export const SCORING_CONFIG = {
  // 地域マッチング（最重要）
  SAME_PREFECTURE: 40, // 同県内
  SAME_REGION: 30,     // 同エリア

  // 認証ランク別スコア
  RANK_SCORES: {
    // Sは旧データ互換
    S: 10,
    A: 8,
    B: 6,
    C: 4,
    D: 2,
  } as const,

  // 最大スコア参考値
  // 同県(40) + 学歴S(10) + 年収S(10) + 精液S(10) + 性病S(10) = 80点
} as const;

/**
 * ドナースコアを計算
 */
export const calculateDonorScore = (
  donorPrefecture: string,
  doneePrefecture: string,
  ranks: {
    education?: VerificationRank | null;
    income?: VerificationRank | null;
    spermTest?: VerificationRank | null;
    stdTest?: VerificationRank | null;
  }
): number => {
  let score = 0;

  // 地域スコア
  if (donorPrefecture === doneePrefecture) {
    score += SCORING_CONFIG.SAME_PREFECTURE;
  } else {
    const donorRegion = getRegionByPrefecture(donorPrefecture);
    const doneeRegion = getRegionByPrefecture(doneePrefecture);
    if (donorRegion && doneeRegion && donorRegion === doneeRegion) {
      score += SCORING_CONFIG.SAME_REGION;
    }
  }

  // ランクスコア
  const rankKeys = ['education', 'income', 'spermTest', 'stdTest'] as const;
  for (const key of rankKeys) {
    const rank = ranks[key];
    if (rank && SCORING_CONFIG.RANK_SCORES[rank]) {
      score += SCORING_CONFIG.RANK_SCORES[rank];
    }
  }

  return score;
};
